import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {
  FirebaseMessageRoomItemPayload,
  GiftShopContentData,
  parseShape,
} from 'types'
import {requestData} from 'services'
import {getFontSize, parseDate, useDidMount, useHistory} from 'utils'
import {Button, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import GiftShopMessageRoomContentList from './GiftShopMessageRoomContentItemList'
import {
  getMessageContentIds,
  getMessagePrice,
  updateMessagePrice,
} from '../GiftshopMessageArrayHelper'

export interface GiftShopMessageRoomContentItemReminderResendProps {
  payload: FirebaseMessageRoomItemPayload['reminder_resend_content']
}

const StyledExtraText = styled(Paragraph)`
  margin-top: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  margin-top: ${convertUnit(20)};
`

export default function GiftShopMessageRoomContentItemReminderResend({
  payload: {
    contents,
    collector_nickname,
    username,
    data: contentData,
    transaction_date,
  },
}: GiftShopMessageRoomContentItemReminderResendProps) {
  const {translate} = useTranslation()
  const [data, setData] = useState<GiftShopContentData[]>([])
  const [loading, setLoading] = useState(true)
  const history = useHistory()
  const contentIds = getMessageContentIds(contentData, contents)
  const prices = getMessagePrice(contentData)

  const shouldRenderButton = useMemo(
    () => data.some(({purchase_status}) => purchase_status === 'resend'),
    [data],
  )

  const handleLoadData = useCallback(
    () =>
      requestData('giftshop_get_contents_detail', {
        params: {content_ids: contents.toString()},
        onRequestSuccess: ({status, data: {result}}) => {
          result = updateMessagePrice(result, contentIds, prices)
          status === 200 && setData(result)
          setLoading(false)
        },
      }),
    [contentIds, contents, prices],
  )

  const handleRenderText: parseShape[] = useMemo(
    () => [
      {
        pattern: /<b>.*?<\/b>/,
        style: {
          fontFamily: 'roboto-bold',
          fontSize: getFontSize('m'),
        },
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
    ],
    [],
  )

  const handleRenderMessage = useMemo(
    () =>
      transaction_date ? (
        <Paragraph fontSize="m">
          <ParsedText
            style={{
              display: 'inline',
            }}
            parse={handleRenderText}>
            {translate('messages:reminderResendContentWithDateFirst', {
              username,
            })}
          </ParsedText>
          <br />
          <br />
          <ParsedText
            style={{
              display: 'inline',
            }}
            parse={handleRenderText}>
            {translate('messages:reminderResendContentWithDateSecond', {
              collector_nickname,
            })}
          </ParsedText>{' '}
          <Paragraph
            style={{
              display: 'inline',
            }}
            fontSize="m"
            fontWeight="bold">
            {parseDate(transaction_date, 'D MMMM YYYY, HH:mm')}
          </Paragraph>
          {'.'}
        </Paragraph>
      ) : (
        <Paragraph fontSize="m">
          {translate('messages:reminderResendContent', {
            username,
            collector_nickname,
          })}
        </Paragraph>
      ),
    [
      transaction_date,
      handleRenderText,
      translate,
      username,
      collector_nickname,
    ],
  )

  const handleRenderContent = useMemo(
    () => (
      <GiftShopMessageRoomContentList
        showFooter
        data={data}
        onClick={(item) =>
          history.push(
            'giftshop_profile_creation_detail',
            {
              data,
              selectedItemId: item.content_id,
              limitContent: true,
            },
            item.creator_name,
          )
        }
        dataCount={contents.length}
        loading={loading}
      />
    ),
    [contents.length, data, history, loading],
  )

  const handleRenderButton = useMemo(
    () => (
      <>
        <StyledExtraText fontSize="m">
          {translate('messages:reminderResendContentExtra')}
        </StyledExtraText>
        {shouldRenderButton && (
          <StyledButton
            label={translate('messages:buttonResendHires')}
            onClick={() =>
              history.push(
                'giftshop_profile',
                {
                  self: true,
                  creationType: 'resend',
                },
                username,
              )
            }
          />
        )}
      </>
    ),
    [history, shouldRenderButton, translate, username],
  )

  useDidMount(() => {
    handleLoadData()
  })

  return (
    <>
      {handleRenderMessage}
      {handleRenderContent}
      {handleRenderButton}
    </>
  )
}
